import { faArrowDownToBracket, faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, useRef, useEffect, useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import '../styles/MediaPlayerStyles.css';

import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { WebVTT } from 'videojs-vtt.js';

import LoadingSpinner from '../../shared/components/UIElements/LoadingSpinner';
import { AuthContext } from '../../shared/context/auth-context';
import { HeaderNavigation } from '../../shared/headerNavigation/HeaderNavigation';
import { useHttpClient } from '../../shared/hooks/http-hook';
import { WhiteBackgroundModal } from '../../shared/modal/whitePopUp/WhiteBackgroundModal';
import WhitePopUpWithLinks from '../../shared/modal/whitePopUp/WhitePopUpWithLinks';
import { initialLocationKey, TrackerType } from '../../shared/util/enum';
import { formatDate, transformSecondsToMinutes } from '../../shared/util/helperfunctions';
import bgImage from '../images/soundwave.jpg';
import { useUpdateProgressMutation } from '../mediaApiSlice';
import { updateMCProgressArray, updateStateProgress } from '../mediaSlice';
import CountdownTimerContainer from './CountdownTimer';
import { PlayerButtons } from './PlayerButtons';
import { ProgrammeCompleted } from './ProgrammeCompleted';
import SnoozeButton from './SnoozeButton';

function SingleMediaPlayer(props) {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const { sendRequest } = useHttpClient();
  const mediaRef = useRef();
  const containerRef = useRef();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [updateProgressAPI] = useUpdateProgressMutation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showCaptions, setShowCaptions] = useState(false);
  const [isAdTurnedOn, setIsAdTurnedOn] = useState(false);
  const [caption, setCaption] = useState('');
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(0.5);
  const [snooze, setSnooze] = useState(false);
  const [snoozeTimeoutId, setSnoozeTimeoutId] = useState(null);
  const [currentTranscriptUrl, setCurrentTranscriptUrl] = useState('');
  const [workoutConsented, setWorkoutConsented] = useState(false);
  const [adToggleState, setAdToggleState] = useState(null);
  const [firstADModalOpen, setFirstADModalOpen] = useState(false);
  const [secondADModalOpen, setSecondADModalOpen] = useState(false);
  const lastUpdateTime = useRef(0);
  const lastMediaRef = useRef(null);

  const timePlayedRef = useRef(0);
  const counterRef = useRef(0);
  const intervalIdRef = useRef(null);

  const runCounter = (start) => {
    if (start) {
      if (intervalIdRef.current === null) {
        intervalIdRef.current = setInterval(() => {
          counterRef.current++;
        }, 1000);
      }
    } else if (intervalIdRef.current !== null) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
      timePlayedRef.current += counterRef.current;
      counterRef.current = 0; // Reset counter
    }
  };

  const mediaContent = useSelector((state) => state.media.singleMedia);
  // hook to hide controls after 3 seconds of inactivity
  useEffect(() => {
    let timeout;
    const resetTimer = () => {
      setShowControls(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => setShowControls(false), 3000);
    };

    const events = ['mousedown', 'mousemove', 'keydown', 'touchstart'];
    events.forEach((event) => document.addEventListener(event, resetTimer));

    timeout = setTimeout(() => setShowControls(false), 3000);

    return () => {
      events.forEach((event) => document.removeEventListener(event, resetTimer));
      clearTimeout(timeout);
    };
  }, []);

  // workout consent logic
  const [consented, setConsented] = useState(false);
  const [openPopUp, setOpenPopUp] = useState(false);

  const checkForConsent = () => {
    if (mediaContent.waiver === true && !consented && mediaContent.category !== 'workout') {
      setOpenPopUp(true);
      return false;
    }
    return true;
  };

  const handleConsent = (value) => {
    const media = mediaRef.current;
    if (value) {
      setConsented(true);
      setIsPlaying(true);
      if ((currentTime * 100) / duration >= 100) {
        media.currentTime = 0;
        setCurrentTime(0);
      }
      media.play();
      runCounter(true);
    } else {
      setConsented(false);
    }
    setOpenPopUp(false);
  };

  // event listeners for media controls
  useEffect(() => {
    const isWorkoutConsentModal = mediaContent?.category === 'workout' && !workoutConsented;

    if (mediaContent?.chid && mediaRef.current && !isWorkoutConsentModal) {
      const media = mediaRef.current;
      if (media) {
        // Set initial video source based on AD state
        media.src = isAdTurnedOn ? mediaContent.url_ad : mediaContent.url;
        media.addEventListener('timeupdate', handleTimeUpdate);
        media.addEventListener('loadedmetadata', handleMetaDataLoad);
        media.addEventListener('volumechange', handleVolumeChange);

        return () => {
          media.removeEventListener('timeupdate', handleTimeUpdate);
          media.removeEventListener('loadedmetadata', handleMetaDataLoad);
          media.removeEventListener('volumechange', handleVolumeChange);
        };
      }
    }
    return () => {};
    // Re-attaches source and listeners only when the media identity or AD state
    // changes (the meaningful triggers, all listed). The handlers read live
    // values through refs/state and are recaptured on those same triggers, so
    // adding them or the derived url/url_ad would reassign media.src on every
    // render and continuously reset playback. Intentionally scoped.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workoutConsented, mediaContent?.chid, mediaContent?.category, isAdTurnedOn]);

  // Handle AD toggle with timeout logic
  useEffect(() => {
    if (!adToggleState) return () => {};

    const { newADState, wasPlaying } = adToggleState;
    const media = mediaRef.current;

    if (!media) {
      setAdToggleState(null);
      return () => {};
    }

    const newSource = newADState ? mediaContent.url_ad : mediaContent.url;

    const timeoutId = setTimeout(() => {
      if (media) {
        media.src = newSource;
        media.load();

        const handleLoadedMetadata = () => {
          setMediaTimeByADState(media, newADState);
          resumePlaybackIfWasPlaying(media, wasPlaying);
          media.removeEventListener('loadedmetadata', handleLoadedMetadata);
        };

        // Either run immediately if metadata is loaded, or wait for it
        if (media.duration) {
          handleLoadedMetadata();
        } else {
          media.addEventListener('loadedmetadata', handleLoadedMetadata);
        }
      }
      setAdToggleState(null); // Clear the state
    }, 100);

    return () => clearTimeout(timeoutId);
    // Runs only when the user toggles AD (adToggleState). The helper functions
    // it calls are recaptured on that trigger and read current values; wrapping
    // them in useCallback would cascade through checkForConsent/runCounter with
    // no behavior gain. Intentionally scoped.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adToggleState, mediaContent?.url_ad, mediaContent?.url]);

  // subtitles and or captions
  const parser = useMemo(() => new WebVTT.Parser(window, WebVTT.StringDecoder()), []);
  const cuesRef = useRef([]);

  parser.oncue = (cue) => {
    const correctedCue = {
      startTime: cue.startTime,
      endTime: cue.endTime,
      text: cue.text,
    };
    cuesRef.current.push(correctedCue);
  };

  const chidRef = useRef(mediaContent.chid);

  useEffect(() => {
    chidRef.current = mediaContent.chid;
    cuesRef.current = [];
  }, [mediaContent.chid]);

  useEffect(() => {
    const transcript = mediaContent?.transcript;

    // Don't load transcript when audio description is active
    if (isAdTurnedOn) {
      cuesRef.current = []; // Clear existing cues
      setCaption(''); // Clear current caption
      setCurrentTranscriptUrl('');
      return;
    }

    if (transcript && currentTranscriptUrl !== transcript) {
      setCurrentTranscriptUrl(transcript);
      cuesRef.current = []; // Clear existing cues

      fetch(transcript)
        .then((response) => response.text())
        .then((data) => {
          if (transcript) {
            parser.parse(data);
            parser.flush();
          }
        });
    }
  }, [currentTranscriptUrl, mediaContent, parser, isAdTurnedOn]);

  const toggleCaptions = () => {
    setShowCaptions(!showCaptions);
  };

  // Helper function to set media time based on AD state
  /* eslint-disable no-param-reassign -- setting .currentTime is the standard way to seek an HTMLMediaElement */
  const setMediaTimeByADState = (media, isADActive) => {
    if (isADActive) {
      // AD content always starts from beginning
      media.currentTime = 0;
      setCurrentTime(0);
      return;
    }

    // Normal content - restore saved progress
    const progress = props.progress || mediaContent.progress;
    const isCompleted = Math.ceil(progress || 0) === 100;

    if (isCompleted || !progress || !media?.duration) {
      media.currentTime = 0;
      setCurrentTime(0);
      return;
    }

    const calculatedTime = (media.duration * progress) / 100;
    const timeToSet = calculatedTime >= 0 ? calculatedTime : 0;
    media.currentTime = timeToSet;
    setCurrentTime(timeToSet);
  };
  /* eslint-enable no-param-reassign */

  // Helper function to resume playback if needed
  const resumePlaybackIfWasPlaying = (media, wasPlaying) => {
    if (!wasPlaying) return;

    let canContinue = true;
    if (mediaContent?.waiver === true) {
      canContinue = checkForConsent();
    }

    if (canContinue) {
      media
        .play()
        .then(() => {
          setIsPlaying(true);
          runCounter(true);
        })
        .catch(() => {});
    }
  };

  const toggleAD = () => {
    const media = mediaRef.current;
    if (!media) return;
    const wasPlaying = isPlaying;
    const newADState = !isAdTurnedOn;

    // Save current progress before switching TO AD mode (reuse updateProgress method)
    if (!isAdTurnedOn && mediaContent?._id && media.duration > 0) {
      updateProgress(media, null, true); // skipCleanup = true to preserve refs
    }

    // Pause current video if playing
    if (!media.paused) {
      media.pause();
      setIsPlaying(false);
      runCounter(false);
    }

    // Update AD state and turn off captions when activating AD
    setIsAdTurnedOn(newADState);
    if (newADState) {
      setShowCaptions(false);
    }

    // Trigger the useEffect to handle the source change
    setAdToggleState({ newADState, wasPlaying });
  };
  // end of subtitles and or captions logic

  // fullscreen event listener
  useEffect(() => {
    const handleFullScreenChange = () => {
      const isNowFullScreen = document.fullscreenElement !== null;
      setIsFullScreen(isNowFullScreen);
      // Restore the fixed height only when leaving fullscreen (covers the ESC
      // key path; the button path restores it in PlayerButtons). Doing it on
      // enter would clamp the video and break fullscreen.
      if (!isNowFullScreen && props.mediaHeight) {
        const video = document.getElementsByTagName('video');
        video[0].style.height = `${props.mediaHeight}px`;
      }
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);

    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
    };
  }, [props.mediaHeight]);

  // snooze button logic for soundscape and sleep story
  const handleSnooze = (time) => {
    // Clear the previous timeout if it exists
    if (snoozeTimeoutId) {
      clearTimeout(snoozeTimeoutId);
      setSnoozeTimeoutId(null);
    }

    // If time is null, just return
    if (time === null) {
      setSnooze(false);
      return;
    }

    const snoozeInSeconds = time * 60 * 1000;
    setSnooze(time);

    const timeoutId = setTimeout(() => {
      setIsPlaying(false);
      const media = mediaRef.current;
      media.pause();
      runCounter(false);
      updateProgress(media);
      setSnooze(false);
    }, snoozeInSeconds);

    // Save the timeout ID so it can be cleared later
    setSnoozeTimeoutId(timeoutId);
  };
  // end of snooze button logic

  const handleVolumeChange = () => {
    const media = mediaRef.current;
    setVolume(media.volume);
    setIsMuted(media.volume === 0);
  };

  useEffect(() => {
    document.documentElement.style.setProperty('--volume-percentage', `${volume * 100}%`);
  }, [volume]);

  const handleTimeUpdate = () => {
    const media = mediaRef.current;
    if (cuesRef.current?.length > 0) {
      const currentCue = cuesRef.current.find(
        (cue) => media.currentTime >= cue.startTime && media.currentTime <= cue.endTime
      );
      if (currentCue) {
        setCaption(currentCue.text);
      } else {
        setCaption('');
      }
    }
    setCurrentTime(media.currentTime);
    if (isAdTurnedOn) return;
    const progressPercent = (media.currentTime / media.duration) * 100;
    const completed = progressPercent >= 99;

    // Get the current time
    const now = Date.now();

    if (
      props.programme &&
      (mediaContent.completed === true || props.progressArray[props.nowActiveIndex] === 100)
    ) {
      // do nothing if programme and current media has already been either marked as completed on the server or has been played to 100% locally
    } else if (
      progressPercent &&
      (now - lastUpdateTime.current >= 10000 || progressPercent === 100)
    ) {
      // Update progress if 10 seconds have passed since the last update or if the media has been played to 100%
      lastUpdateTime.current = now;
      updateProgressAPI({
        token: auth.token,
        mediaId: mediaContent._id,
        progress: progressPercent,
        completed,
      }).catch(() => {});
      dispatch(
        updateStateProgress({
          progress: progressPercent,
          completed,
        })
      );
    }

    // update masterclass progress array if masterclass and not programme or if programme and current media is not completed
    if (
      (props.masterclass && !props.programme) ||
      (props.programme && props.progressArray && props.progressArray[props.nowActiveIndex] < 100)
    ) {
      dispatch(
        updateMCProgressArray({
          id: mediaContent._id,
          progress: progressPercent,
        })
      );
    }
    // if the media has been played to 100% and if it is a masterclass, pause the media and autoplay the next chapter
    if (progressPercent === 100 && props.masterclass && !props.programme) {
      media.pause();
      runCounter(false);
      props.handleAutoPlay();
    }

    // if the media has been played to 100% and if it is a programme, call programme autoplay
    if (progressPercent === 100 && props.programme) {
      props.handleProgAutoPlay();
    }
  };

  const updateMindfulMinutes = async (time) => {
    // check if today's tracker has already been submitted and update or create a new tracker
    const date = new Date();
    const trackerDate = formatDate(date);
    let tracker;
    let trackerId;
    let data;
    let url;
    let method;
    let updatedTracker;

    try {
      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/trackers/mood/${trackerDate}`
      );
      if (response && response.tracker?._id) {
        tracker = response.tracker;
        trackerId = response.tracker._id;
        data = JSON.parse(tracker.data);
      }

      if (!!tracker && !!trackerId) {
        method = 'PUT';
        url = `${process.env.REACT_APP_BACKEND_URL}/trackers/${trackerId}`;
        updatedTracker = {
          type: TrackerType.Mood,
          data: {
            mood_rating: data.mood_rating,
            mindful_minutes: [
              { off_platform: data.mindful_minutes[0]?.off_platform },
              {
                in_platform:
                  Number(data.mindful_minutes[1]?.in_platform) + transformSecondsToMinutes(time),
              },
            ],
          },
          hidden: tracker.hidden,
          platform: 'web',
          enhancers: tracker.enhancers,
          inhibitors: [],
          journal: tracker.journal,
          trackerDate,
        };
      } else {
        method = 'POST';
        url = `${process.env.REACT_APP_BACKEND_URL}/trackers/`;

        updatedTracker = {
          type: TrackerType.Mood,
          data: {
            mood_rating: null,
            mindful_minutes: [
              { off_platform: 0 },
              { in_platform: transformSecondsToMinutes(time) },
            ],
          },
          hidden: false,
          platform: 'web',
          enhancers: [],
          inhibitors: [],
          journal: '',
          trackerDate,
        };
      }
    } catch (err) {}

    if (updatedTracker && url && method) {
      try {
        await sendRequest(url, method, JSON.stringify(updatedTracker));
      } catch (err) {}
    }
  };

  // Function to update progress
  const updateProgress = async (media, timePlayed, skipCleanup = false) => {
    // Only update progress for normal content, not for audio description
    if (media?.duration > 0 && !isAdTurnedOn && mediaContent?._id) {
      const progressPercent = (media.currentTime / media.duration) * 100;
      const completed = progressPercent >= 99;
      if (progressPercent < 0) return;
      try {
        await updateProgressAPI({
          token: auth.token,
          mediaId: mediaContent._id,
          progress: progressPercent,
          completed,
        });

        dispatch(
          updateStateProgress({
            progress: progressPercent,
            completed,
          })
        );

        if (!!mediaContent.mindfulness && timePlayed) {
          updateMindfulMinutes(timePlayed);
        }
      } catch (err) {}

      if (!skipCleanup) {
        lastUpdateTime.current = 0;
        lastMediaRef.current = null;
        timePlayedRef.current = 0;
        counterRef.current = 0;
        intervalIdRef.current = null;
      }
    }
  };

  const handleMetaDataLoad = () => {
    const media = mediaRef.current;

    // Ensure duration is valid before using it
    if (!media?.duration || media.duration <= 0) {
      return;
    }

    setDuration(Math.round(media.duration));

    // If AD is active, always start from beginning. Otherwise use saved progress
    if (isAdTurnedOn) {
      setCurrentTime(0);
      media.currentTime = 0;
    } else {
      const progress = props.progress || mediaContent.progress;
      if (Math.ceil(progress || 0) === 100) {
        setCurrentTime(0);
        media.currentTime = 0;
      } else if (progress && media?.duration) {
        const calculatedTime = (media.duration * progress) / 100;
        if (calculatedTime >= 0) {
          media.currentTime = calculatedTime;
          setCurrentTime(calculatedTime);
        } else {
          media.currentTime = 0;
          setCurrentTime(0);
        }
      } else {
        setCurrentTime(0);
        media.currentTime = 0;
      }
    }

    let canContinue = true;
    if (mediaContent?.waiver === true) {
      canContinue = checkForConsent();
    }

    if (mediaRef.current?.src && props.masterclass && props.keepPlaying && canContinue) {
      setIsPlaying(true);
      mediaRef.current.play();
      runCounter(true);
    } else {
      setIsPlaying(false);
      mediaRef.current?.pause();
      runCounter(false);
    }
  };

  useEffect(() => {
    // Listen for user navigating away via browser back/forward buttons
    const unlisten = () => {
      runCounter(false);
      if (!props.masterclass && !props.programme && !isAdTurnedOn) {
        updateProgress(lastMediaRef.current, timePlayedRef.current);
      }
    };

    // Clean up the event listener on component unmount
    return () => {
      unlisten();
    };
    // Flush progress exactly once per route change / unmount. It reads the
    // latest media and time through refs (lastMediaRef/timePlayedRef), so
    // adding updateProgress or the masterclass/programme flags would fire the
    // cleanup on every render and loop the progress dispatch. Intentionally scoped.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, isAdTurnedOn]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem('pageReload', true);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const handleNavigation = () => {
    navigate(-1);
  };

  useEffect(() => {
    // Update lastMediaRef with the current mediaRef value that will persist through renders
    lastMediaRef.current = mediaRef.current;
  });

  const getPopUpContents = (isPopUpOpen) => {
    return {
      title: t('M008'),
      heading: `${t('M009')}...`,
      paragraphs: [t('M010')],
      backBtn: {
        text: t('back.button'),
        link: '/for-you',
      },
      continueBtn: {
        text: t('M012'),
        handleClick: () => {
          if (isPopUpOpen) {
            handleConsent(true);
          } else {
            setWorkoutConsented(true);
          }
        },
      },
    };
  };

  const getPlayerClassName = () => {
    let baseClassName;
    if (props.masterclass) {
      baseClassName = mediaContent.type === 'audio' ? 'mcplayer' : 'mcplayer video';
    } else {
      baseClassName = mediaContent.type === 'audio' ? 'media-player' : 'media-player video';
    }
    return baseClassName + (mediaContent.snooze || mediaContent.signposting ? ' has-controls' : '');
  };

  const getPlayerBackgroundImage = () => {
    if (props.masterclass) {
      if (mediaContent.type === 'audio') {
        return `url(${mediaContent.video_cover_image ? mediaContent.video_cover_image : bgImage})`;
      }
      return 'none';
    }
    if (mediaContent.type === 'audio') {
      return `url(${
        mediaContent.background_image
          ? mediaContent.background_image
          : mediaContent.video_cover_image
      })`;
    }
    return 'none';
  };

  return (
    <>
      {true && !props.masterclass && (
        <HeaderNavigation
          bottomBarActionButton={
            location.key !== initialLocationKey && {
              ariaLabel: t('nav.button.back'),
              onClick: handleNavigation,
              icon: {
                faIconDefinition: faChevronLeft,
                size: 'lg',
              },
              variant: 'icon-only',
              className: 'back-button',
            }
          }
        />
      )}
      <div className={props.masterclass ? '' : 'main-background'}>
        {!mediaContent && <LoadingSpinner />}
        {mediaContent &&
          (mediaContent?.category === 'workout' && !workoutConsented ? (
            <WhitePopUpWithLinks
              popUpContents={getPopUpContents(openPopUp)}
              dialogTitle={t('modal.workout.consent')}
            />
          ) : (
            <div
              className={getPlayerClassName()}
              style={{
                ...(props.mediaHeight && {
                  height: `${props.mediaHeight}px`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'top center',
                  backgroundRepeat: 'no-repeat',
                }),
                backgroundImage: getPlayerBackgroundImage(),
              }}
            >
              <div className={props.masterclass ? '' : 'background-blur'}>
                <div ref={containerRef} className="container-ref">
                  {props.progCompletedOpen && (
                    <ProgrammeCompleted closeProgrammeCompleted={props.closeProgrammeCompleted} />
                  )}
                  {props.openCountdown && (
                    <CountdownTimerContainer
                      setOpenModal={props.setOpenCountdown}
                      playNext={props.playNext}
                    />
                  )}
                  {openPopUp && (
                    <WhitePopUpWithLinks
                      popUpContents={getPopUpContents(openPopUp)}
                      dialogTitle={t('modal.workout.consent')}
                    />
                  )}
                  {mediaContent.type === 'audio' && (
                    <>
                      {isFullScreen && (
                        <div
                          className="audio-fullscreen"
                          style={{
                            backgroundImage: `url(${bgImage})`,
                            backgroundSize: 'cover',
                            height: '100vh',
                          }}
                        />
                      )}
                      {/* eslint-disable-next-line jsx-a11y/media-has-caption -- captions are rendered by the custom WebVTT overlay (subtitles-container below) fed from mediaContent.transcript; a native track would duplicate it */}
                      <audio
                        style={{
                          ...(props.mediaHeight && {
                            height: `${props.mediaHeight}px`,
                          }),
                        }}
                        ref={mediaRef}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowControls(!showControls);
                        }}
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                      >
                        <source src={mediaContent.url} type="audio/mp3" />
                      </audio>
                    </>
                  )}
                  {mediaContent.type === 'video' && mediaContent.category !== 'soundscape' && (
                    // eslint-disable-next-line jsx-a11y/media-has-caption -- captions are rendered by the custom WebVTT overlay (subtitles-container below) fed from mediaContent.transcript; a native track would duplicate it
                    <video
                      style={{
                        ...(props.mediaHeight &&
                          !isFullScreen && {
                            height: `${props.mediaHeight}px`,
                          }),
                      }}
                      ref={mediaRef}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowControls(!showControls);
                      }}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                      disablePictureInPicture
                    >
                      <source
                        src={isAdTurnedOn ? mediaContent?.url_ad : mediaContent?.url}
                        type="video/mp4"
                      />
                    </video>
                  )}
                  {mediaContent.category === 'soundscape' && (
                    // eslint-disable-next-line jsx-a11y/media-has-caption -- soundscapes have no dialogue; captions, when a transcript exists, come from the same WebVTT overlay as above
                    <video
                      ref={mediaRef}
                      loop
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowControls(!showControls);
                      }}
                      poster={mediaContent.video_cover_image}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                      disablePictureInPicture
                    >
                      <source src={mediaContent.url} type="video/mp4" />
                    </video>
                  )}
                  {showCaptions &&
                    caption?.length > 0 &&
                    mediaContent?.transcript &&
                    !isAdTurnedOn && (
                      <div
                        className={
                          showControls ? 'subtitles-container raised' : 'subtitles-container'
                        }
                      >
                        {caption}
                      </div>
                    )}
                  <div className="controls container" style={{ opacity: showControls ? 1 : 0 }}>
                    {!props.masterclass && (
                      <div className="heading">
                        <h3 className="title">{mediaContent.title}</h3>
                        <p className="author">
                          {t('M088', {
                            author: mediaContent?.author?.author ?? 'Champion Health',
                          })}
                        </p>
                      </div>
                    )}
                    <PlayerButtons
                      class="player"
                      mediaRef={mediaRef}
                      mediaContent={mediaContent}
                      volume={volume}
                      setVolume={setVolume}
                      isMuted={isMuted}
                      setIsMuted={setIsMuted}
                      duration={duration}
                      currentTime={currentTime}
                      setCurrentTime={setCurrentTime}
                      isFullScreen={isFullScreen}
                      setIsFullScreen={setIsFullScreen}
                      isPlaying={isPlaying}
                      setIsPlaying={setIsPlaying}
                      showCaptions={showCaptions}
                      toggleCaptions={toggleCaptions}
                      containerRef={containerRef}
                      mediaHeight={props.mediaHeight}
                      checkForConsent={checkForConsent}
                      runCounter={runCounter}
                      showAD={!!mediaContent?.url_ad}
                      toggleAD={toggleAD}
                      isAdTurnedOn={isAdTurnedOn}
                      openFirstModal={() => setFirstADModalOpen(true)}
                      openSecondModal={() => setSecondADModalOpen(true)}
                    />
                    {!props.masterclass && (
                      <div className="action-container">
                        {!!mediaContent.snooze && isPlaying ? (
                          <SnoozeButton
                            handleSnooze={handleSnooze}
                            isPlaying={isPlaying}
                            snooze={snooze}
                          />
                        ) : (
                          <>
                            {mediaContent.signposting && (
                              <a
                                href={mediaContent.signposting}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <div className="mimicButton">
                                  <FontAwesomeIcon
                                    icon={faArrowUpRightFromSquare}
                                    className="icon"
                                  />
                                  {t('M001')}
                                </div>
                              </a>
                            )}
                            {mediaContent.resources && (
                              <a
                                href={mediaContent.resources}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <div className="mimicButton">
                                  <FontAwesomeIcon icon={faArrowDownToBracket} className="icon" />{' '}
                                  {t('M002')}
                                </div>
                              </a>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
      </div>
      <div className="ad-modals">
        {firstADModalOpen && (
          <WhiteBackgroundModal
            size="small"
            id="player-ad-modal_first"
            hasCloseIcon={false}
            title={{ text: t('player.ad.modal.title'), showTitle: true }}
            submitButton={{
              text: t('player.ad.modal.button'),
              onClick: () => {
                setFirstADModalOpen(false);
                toggleAD();
              },
            }}
            cancelButton={{
              text: t('player.ad.modal.cancel'),
              onClick: () => {
                setFirstADModalOpen(false);
              },
            }}
            handleClose={() => setFirstADModalOpen(false)}
          >
            <div className="player-ad-modal-content">
              <p className="description">{t('player.ad.modal.description')}</p>
              <p className="description-second">{t('player.ad.modal.description.second')}</p>
            </div>
          </WhiteBackgroundModal>
        )}
        {secondADModalOpen && (
          <WhiteBackgroundModal
            size="small"
            id="player-ad-modal_second"
            hasCloseIcon={false}
            title={{ text: t('player.ad.modal.second.title'), showTitle: true }}
            submitButton={{
              text: t('player.ad.modal.second.button'),
              onClick: () => {
                setSecondADModalOpen(false);
                toggleAD();
              },
            }}
            cancelButton={{
              text: t('player.ad.modal.second.cancel'),
              onClick: () => setSecondADModalOpen(false),
            }}
            handleClose={() => setSecondADModalOpen(false)}
          >
            <div className="player-ad-modal-content">
              <p className="description">{t('player.ad.modal.second.description')}</p>
            </div>
          </WhiteBackgroundModal>
        )}
      </div>
    </>
  );
}
export default SingleMediaPlayer;
